import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

const CATS = [
  { k: "all", l: "All" },
  { k: "business", l: "Business Insights" },
  { k: "career", l: "Career Insights" },
];

export default function Resources() {
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [posts, setPosts] = useState([]);
  const [email, setEmail] = useState("");

  const load = useCallback(() => {
    api.get("/blog", { params: { category: cat, search: search || undefined } })
      .then((r) => setPosts(r.data)).catch(() => {});
  }, [cat, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Insights incoming.");
      setEmail("");
    } catch { toast.error("Please try again."); }
  };

  const featured = posts.filter((p) => p.featured);
  const rest = posts.filter((p) => !p.featured);

  return (
    <div data-testid="resources-page">
      <SEO title="Resources" description="Business and career insights: hiring, onboarding, HR operations, resume writing, interview prep, LinkedIn strategy, and more." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-10">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">Resources</span>
          <h1 className="font-serif text-5xl md:text-7xl text-espresso mt-5 leading-[1.02]">Insights worth your time.</h1>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mt-10">
            <div className="inline-flex border border-champagne/40 rounded-full p-1.5 flex-wrap">
              {CATS.map((c) => (
                <button key={c.k} onClick={() => setCat(c.k)} data-testid={`resources-cat-${c.k}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${cat === c.k ? "bg-espresso text-ivory" : "text-espresso hover:text-champagne"}`}>
                  {c.l}
                </button>
              ))}
            </div>
            <div className="relative max-w-sm w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-espresso/40" size={18} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles…" data-testid="resources-search"
                className="w-full bg-ivory border border-champagne/40 rounded-full pl-11 pr-4 py-3 text-espresso focus:outline-none focus:ring-2 focus:ring-champagne" />
            </div>
          </div>
        </Reveal>
      </section>

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {featured.slice(0, 2).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to={`/resources/${p.slug}`} data-testid={`resources-featured-${p.slug}`} className="group block border-t border-espresso/15 pt-8">
                  <span className="text-xs uppercase tracking-[0.25em] text-champagne">{p.category === "business" ? "Business Insights" : "Career Insights"} · Featured</span>
                  <h3 className="font-serif text-3xl md:text-4xl text-espresso mt-4 leading-[1.05] group-hover:text-champagne transition-colors">{p.title}</h3>
                  {p.excerpt && <p className="text-espresso/70 mt-4 leading-relaxed max-w-xl">{p.excerpt}</p>}
                  <span className="inline-flex items-center gap-1 text-espresso font-medium text-sm mt-5 group-hover:text-champagne transition-colors">Read more <ArrowUpRight size={16} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {rest.length === 0 && featured.length === 0 ? (
          <p className="text-espresso/60 text-center py-12">No articles found. Try a different search or category.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Link to={`/resources/${p.slug}`} data-testid={`resources-post-${p.slug}`} className="group block border-t border-espresso/15 pt-6 h-full">
                  <span className="text-xs uppercase tracking-[0.2em] text-champagne">{p.category === "business" ? "Business Insights" : "Career Insights"}</span>
                  <h3 className="font-serif text-2xl text-espresso mt-3 leading-snug group-hover:text-champagne transition-colors">{p.title}</h3>
                  <p className="text-espresso/70 text-sm mt-3 line-clamp-3 leading-relaxed">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-espresso font-medium text-sm mt-4 group-hover:text-champagne transition-colors">Read more <ArrowUpRight size={16} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-espresso/10">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-espresso">Get insights in your inbox.</h2>
          <p className="text-espresso/70 mt-3">Thoughtful HR and career guidance, no noise.</p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 mt-8 max-w-md mx-auto" data-testid="resources-newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="resources-newsletter-input"
              className="flex-1 bg-ivory border border-champagne/40 rounded-full px-5 py-3 text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-2 focus:ring-champagne" />
            <button type="submit" data-testid="resources-newsletter-submit" className="bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors">Subscribe</button>
          </form>
        </div>
      </section>
    </div>
  );
}
